import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

export default function RetirarPaquete({ token }) {
  const [paquete, setPaquete] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [nombre, setNombre] = useState('')
  const [firmando, setFirmando] = useState(false)
  const [confirmado, setConfirmado] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasFirma, setHasFirma] = useState(false)
  const canvasRef = useRef(null)
  const lastPos = useRef(null)

  useEffect(() => { fetchPaquete() }, [token])

  const fetchPaquete = async () => {
    const { data, error } = await supabase
      .from('paquetes')
      .select('*, oficinas(numero, titular), edificios(nombre)')
      .eq('token_retiro', token)
      .single()
    if (error || !data) { setError('Paquete no encontrado o link inválido.'); setLoading(false); return }
    if (data.estado === 'retirado') { setError('Este paquete ya fue retirado.'); setLoading(false); return }
    setPaquete(data)
    setLoading(false)
  }

  // Canvas drawing
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY }
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }

  const startDraw = (e) => {
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pos = getPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
    lastPos.current = pos
    setIsDrawing(true)
    setHasFirma(true)
  }

  const draw = (e) => {
    e.preventDefault()
    if (!isDrawing) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pos = getPos(e, canvas)
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
    lastPos.current = pos
  }

  const endDraw = (e) => { e?.preventDefault(); setIsDrawing(false) }

  const limpiarFirma = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasFirma(false)
  }

  const handleConfirmar = async () => {
    if (!nombre.trim()) { alert('Por favor ingresá tu nombre completo'); return }
    if (!hasFirma) { alert('Por favor firmá antes de confirmar'); return }
    setFirmando(true)

    try {
      const canvas = canvasRef.current
      const firmaBase64 = canvas.toDataURL('image/png')

      const { error: updErr } = await supabase.from('paquetes').update({
        estado: 'retirado',
        fecha_retiro: new Date().toISOString(),
        firma_url: firmaBase64,
        retirado_por_nombre: nombre.trim(),
      }).eq('token_retiro', token)

      if (updErr) throw updErr
      
      // Enviar email con la firma
      await enviarEmailRetiro(firmaBase64, nombre.trim())
      
      setConfirmado(true)
    } catch (e) {
      alert('Error: ' + (e?.message || e?.error_description || JSON.stringify(e)))
      console.error(e)
    }
    setFirmando(false)
  }

  const enviarEmailRetiro = async (firmaBase64, nombreRetiro) => {
    try {
      // Obtener datos completos del paquete
      const { data: paqueteCompleto } = await supabase
        .from('paquetes')
        .select('*, oficinas(numero, titular, email_contacto, email_contacto_2), edificios(nombre)')
        .eq('token_retiro', token)
        .single()

      if (!paqueteCompleto) return

      // Armar emails de destino (contacto principal + secundario de la oficina)
      const emails = [paqueteCompleto.oficinas?.email_contacto]
        .concat(paqueteCompleto.oficinas?.email_contacto_2 || [])
        .filter(Boolean)

      if (emails.length === 0) return // Sin emails, no enviar

      // Enviar email a cada contacto
      for (const email of emails) {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: 'paquete_retiro',
            destinatario_email: email,
            oficina: paqueteCompleto.oficinas?.numero || '',
            oficina_titular: paqueteCompleto.oficinas?.titular || '',
            edificio: paqueteCompleto.edificios?.nombre || '',
            datos: {
              destinatario: paqueteCompleto.destinatario,
              numero_envio: paqueteCompleto.numero_envio,
              observaciones: paqueteCompleto.observaciones,
              retirado_por_nombre: nombreRetiro,
              firma_url: firmaBase64
            }
          })
        }).catch(err => console.error('Error enviando email:', err))
      }
    } catch (e) {
      console.error('Error en enviarEmailRetiro:', e)
      // No romper el flujo si falla el email
    }
  }

  const formatDate = (d) => new Date(d).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F0E8' }}>
      <div style={{ color: '#666', fontFamily: 'sans-serif' }}>Cargando...</div>
    </div>
  )

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F0E8', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>{error}</div>
        <div style={{ fontSize: 14, color: '#666' }}>Si creés que esto es un error, consultá con recepción.</div>
      </div>
    </div>
  )

  if (confirmado) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F0E8', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#4A9B6F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, margin: '0 auto 20px' }}>✓</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 8 }}>Retiro confirmado</div>
        <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Gracias, <strong>{nombre}</strong>.</div>
        <div style={{ fontSize: 13, color: '#999' }}>Tu firma quedó registrada. Podés cerrar esta ventana.</div>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#F5F0E8', padding: '32px 16px', fontFamily: 'sans-serif' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 13, color: '#999', letterSpacing: 2, marginBottom: 6, textTransform: 'uppercase' }}>Edificios Essence</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>Confirmación de retiro</div>
        </div>

        {/* Info paquete */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 11, color: '#999', letterSpacing: 1.5, marginBottom: 12, textTransform: 'uppercase' }}>Detalle del paquete</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
            <div>
              <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>DESTINATARIO</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{paquete.destinatario}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>OFICINA</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>Of. {paquete.oficinas?.numero}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>EDIFICIO</div>
              <div style={{ fontSize: 14, color: '#444' }}>{paquete.edificios?.nombre}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>RECIBIDO</div>
              <div style={{ fontSize: 14, color: '#444' }}>{formatDate(paquete.created_at)}</div>
            </div>
            {paquete.numero_envio && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>N° DE ENVÍO</div>
                <div style={{ fontSize: 13, color: '#444', fontFamily: 'monospace' }}>#{paquete.numero_envio}</div>
              </div>
            )}
            {paquete.observaciones && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>OBSERVACIONES</div>
                <div style={{ fontSize: 13, color: '#444' }}>{paquete.observaciones}</div>
              </div>
            )}
          </div>
        </div>

        {/* Nombre */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <label style={{ display: 'block', fontSize: 11, color: '#999', letterSpacing: 1.5, marginBottom: 10, textTransform: 'uppercase' }}>
            Tu nombre completo
          </label>
          <input
            type="text"
            placeholder="Ej: Juan García"
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e0e0e0', fontSize: 16, outline: 'none', boxSizing: 'border-box', color: '#1a1a1a' }}
          />
        </div>

        {/* Firma */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#999', letterSpacing: 1.5, textTransform: 'uppercase' }}>Firma</div>
            {hasFirma && (
              <button onClick={limpiarFirma} style={{ fontSize: 12, color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px' }}>
                Borrar y volver a firmar
              </button>
            )}
          </div>
          <div style={{ border: '1.5px dashed #d0d0d0', borderRadius: 10, overflow: 'hidden', background: '#fafafa', touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              style={{ width: '100%', height: 160, display: 'block', cursor: 'crosshair' }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
          </div>
          {!hasFirma && (
            <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', marginTop: 8 }}>
              Firmá en el recuadro de arriba con el dedo o el mouse
            </div>
          )}
        </div>

        {/* Botón confirmar */}
        <button
          onClick={handleConfirmar}
          disabled={firmando || !nombre.trim() || !hasFirma}
          style={{
            width: '100%', padding: '16px', borderRadius: 12, border: 'none', cursor: firmando || !nombre.trim() || !hasFirma ? 'not-allowed' : 'pointer',
            background: firmando || !nombre.trim() || !hasFirma ? '#ccc' : '#C9A84C',
            color: '#fff', fontSize: 16, fontWeight: 700, transition: 'background 0.2s'
          }}
        >
          {firmando ? 'Confirmando...' : 'Confirmar retiro'}
        </button>

        <div style={{ textAlign: 'center', fontSize: 11, color: '#bbb', marginTop: 16 }}>
          Al confirmar, quedás registrado como responsable del retiro de este paquete.
        </div>
      </div>
    </div>
  )
}
